import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  ChevronDown, 
  ChevronUp, 
  HelpCircle, 
  Mail, 
  Globe, 
  Lightbulb,
  Users,
  Building2,
  BookOpen,
  Star,
  Shield,
  Zap,
  Target,
  ArrowRight,
  Sparkles
} from 'lucide-react';

// Animation variants
const fadeUpVariants = {
  hidden: { 
    opacity: 0, 
    y: 60,
    scale: 0.95
  },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1
  }
};

const scaleVariants = {
  hidden: { 
    opacity: 0, 
    scale: 0.8,
    y: 20
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0
  }
};

const pulseVariants = {
  hover: {
    scale: 1.05
  },
  tap: {
    scale: 0.95
  }
};

// FAQ Data - will be populated with translations
const getFaqData = (t: any) => [
  {
    id: 1,
    question: t('faq.questions.whatIsDirectory'),
    answer: t('faq.answers.whatIsDirectory'),
    icon: BookOpen,
    category: "general"
  },
  {
    id: 2,
    question: t('faq.questions.whoIsItFor'),
    answer: t('faq.answers.whoIsItFor'),
    icon: Users,
    category: "audience"
  },
  {
    id: 3,
    question: t('faq.questions.differenceFromPlatform'),
    answer: t('faq.answers.differenceFromPlatform'),
    icon: Target,
    category: "general"
  },
  {
    id: 4,
    question: t('faq.questions.whoCanBeReferenced'),
    answer: t('faq.answers.whoCanBeReferenced'),
    icon: Building2,
    category: "partners"
  },
  {
    id: 5,
    question: t('faq.questions.howToRegister'),
    answer: t('faq.answers.howToRegister'),
    icon: Zap,
    category: "registration"
  },
  {
    id: 6,
    question: t('faq.questions.canRecommend'),
    answer: t('faq.answers.canRecommend'),
    icon: Star,
    category: "community"
  },
  {
    id: 7,
    question: t('faq.questions.isReferencingPaid'),
    answer: t('faq.answers.isReferencingPaid'),
    icon: Shield,
    category: "pricing"
  },
  {
    id: 8,
    question: t('faq.questions.canReferenceConsulting'),
    answer: t('faq.answers.canReferenceConsulting'),
    icon: Lightbulb,
    category: "services"
  },
  {
    id: 9,
    question: t('faq.questions.howToUpdate'),
    answer: t('faq.answers.howToUpdate'),
    icon: Zap,
    category: "management"
  },
  {
    id: 10,
    question: t('faq.questions.updateFrequency'),
    answer: t('faq.answers.updateFrequency'),
    icon: Sparkles,
    category: "updates"
  },
  {
    id: 11,
    question: t('faq.questions.whyJoinDirectory'),
    answer: t('faq.answers.whyJoinDirectory'),
    icon: ArrowRight,
    category: "benefits"
  },
  {
    id: 12,
    question: t('faq.questions.howToContact'),
    answer: t('faq.answers.howToContact'),
    icon: Mail,
    category: "contact"
  }
];

const FaqPage: React.FC = () => {
  const { t } = useTranslation();
  const [openItems, setOpenItems] = useState<number[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqData = getFaqData(t);

  const categories = [
    { id: 'all', name: t('faq.categories.all'), icon: HelpCircle },
    { id: 'general', name: t('faq.categories.general'), icon: BookOpen },
    { id: 'audience', name: t('faq.categories.audience'), icon: Users },
    { id: 'partners', name: t('faq.categories.partners'), icon: Building2 },
    { id: 'registration', name: t('faq.categories.registration'), icon: Zap },
    { id: 'community', name: t('faq.categories.community'), icon: Star },
    { id: 'pricing', name: t('faq.categories.pricing'), icon: Shield },
    { id: 'services', name: t('faq.categories.services'), icon: Lightbulb },
    { id: 'management', name: t('faq.categories.management'), icon: Zap },
    { id: 'updates', name: t('faq.categories.updates'), icon: Sparkles },
    { id: 'benefits', name: t('faq.categories.benefits'), icon: ArrowRight },
    { id: 'contact', name: t('faq.categories.contact'), icon: Mail }
  ];

  const toggleItem = (id: number) => {
    setOpenItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const filteredFaqs = selectedCategory === 'all' 
    ? faqData 
    : faqData.filter(faq => faq.category === selectedCategory);

  return (
    <div className="min-h-screen bg-brand-surface">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-brand-surface">
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div 
            className="text-center"
            initial="hidden"
            animate="visible"
            variants={fadeUpVariants}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.div 
              className="inline-flex items-center justify-center w-20 h-20 bg-brand-primary rounded-2xl mb-8 shadow-lg"
              variants={scaleVariants}
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ duration: 0.2 }}
            >
              <HelpCircle className="h-10 w-10 text-white" />
            </motion.div>
            
            <motion.h1 
              className="text-5xl font-bold text-gray-900 mb-6"
              variants={fadeUpVariants}
            >
              💡 {t('faq.title')}
            </motion.h1>
            
            <motion.p 
              className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
              variants={fadeUpVariants}
            >
              {t('faq.subtitle')}
            </motion.p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Category Filter */}
        <motion.div 
          className="mb-12"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          transition={{ staggerChildren: 0.1 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            {t('faq.browseByCategory')}
          </h2>
          
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <motion.button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                    selectedCategory === category.id
                      ? 'bg-brand-primary text-white shadow-lg'
                      : 'bg-white text-gray-600 hover:bg-brand-primary/10 hover:text-brand-primary border border-gray-200'
                  }`}
                  variants={scaleVariants}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <IconComponent className="h-4 w-4" />
                  {category.name}
                </motion.button>
              );
            })}
          </div>
        </motion.div>

        {/* FAQ Items */}
        <motion.div 
          className="space-y-6"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          transition={{ staggerChildren: 0.1 }}
        >
          {filteredFaqs.map((faq, index) => {
            const IconComponent = faq.icon;
            const isOpen = openItems.includes(faq.id);
            
            return (
              <motion.div
                key={faq.id}
                className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300"
                variants={fadeUpVariants}
                whileHover={{ y: -2 }}
              >
                <button
                  onClick={() => toggleItem(faq.id)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-12 h-12 bg-brand-primary rounded-xl flex items-center justify-center shadow-md">
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                  </div>
                  
                  <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex-shrink-0"
                  >
                    {isOpen ? (
                      <ChevronUp className="h-6 w-6 text-brand-primary" />
                    ) : (
                      <ChevronDown className="h-6 w-6 text-gray-400" />
                    )}
                  </motion.div>
                </button>
                
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6">
                        <div className="bg-brand-primary/5 rounded-xl p-6 border border-brand-primary/20">
                          <div className="prose prose-gray max-w-none">
                            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                              {faq.answer}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Contact Section */}
        <motion.div 
          className="mt-16 bg-brand-primary/10 rounded-2xl p-8 border border-brand-primary/20"
          initial="hidden"
          animate="visible"
          variants={fadeUpVariants}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="text-center">
            <motion.div 
              className="inline-flex items-center justify-center w-16 h-16 bg-brand-primary rounded-xl mb-6 shadow-lg"
              variants={scaleVariants}
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ duration: 0.2 }}
            >
              <Mail className="h-8 w-8 text-white" />
            </motion.div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('faq.contact.title')}
            </h2>
            
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              {t('faq.contact.subtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.a
                href="mailto:info@smartpillsolutions.com"
                className="flex items-center gap-2 px-6 py-3 bg-brand-primary text-white rounded-xl hover:bg-brand-secondary transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                variants={pulseVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <Mail className="h-5 w-5" />
                info@smartpillsolutions.com
              </motion.a>
              
              <motion.a
                href="https://www.smartpillsolutions.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-3 border-2 border-brand-primary text-brand-primary rounded-xl hover:bg-brand-primary hover:text-white transition-all duration-200 font-semibold"
                variants={pulseVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <Globe className="h-5 w-5" />
                www.smartpillsolutions.com
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FaqPage;
